import { computed, inject, Injectable, Signal } from '@angular/core';
import { EntityDetailStore } from '@webapp/core/stores/entity-detail-store.service';
import { EntityListStore } from '@webapp/core/stores/entity-list-store.service';
import { PaginationParams } from '@webapp/core/ui/components/pagination/pagination.interfaces';
import { firstValueFrom, map } from 'rxjs';
import { buildStatus } from '../common/map';
import { ICreateAdminPayload, IUserDetail, IUserRow, USER_ROLES, UserStatusValue } from '../interfaces/users.interface';
import { UsersService } from '../services/users.service';
import { UserStatusService } from '../services/user-status.service';

@Injectable({
	providedIn: 'root',
})
export class UsersStoreService {
	listStore: EntityListStore<IUserRow>;
	detailStore: EntityDetailStore<IUserDetail>;
	service = inject(UsersService);
	private readonly userStatusService = inject(UserStatusService);

	constructor() {
		this.listStore = new EntityListStore<IUserRow>(
			(params: PaginationParams) =>
				firstValueFrom(
					this.service.get(params).pipe(
						map(res => ({
							...res,
							data: res.data.map(user => ({
								...user,
								...buildStatus(user),
							})),
						}))
					)
				)
		);
		this.detailStore = new EntityDetailStore<IUserDetail>((id: string) =>
			firstValueFrom(
				this.service.getById(id).pipe(
					map(res =>
						res
							? ({
								...res,
								...buildStatus(res),
							} as unknown as IUserDetail)
							: null
					)
				)
			)
		);
	}

	hasProspectFilter(): Signal<boolean> {
		return computed(
			() =>
				this.listStore.select('filters')()?.['role'] ===
				USER_ROLES.PROSPECT
		);
	}

	hasAgentsFilter(): Signal<boolean> {
		return computed(
			() =>
				this.listStore.select('filters')()?.['role'] ===
				USER_ROLES.AGENT
		);
	}

	public getRows() {
		return this.listStore.select('rows');
	}

	public get() {
		return this.detailStore.getSelected();
	}

	public getLoading() {
		return this.detailStore.select('loading');
	}

	async changeUserStatus(id: string, status: UserStatusValue) {
		const success = await this.userStatusService.changeStatus(id, status);
		if (success) {
			await this.detailStore.fetch(id);
		}
	}

	// Client agent ("PAS") reassignment. Errors are caught here (not surfaced)
	// because the HttpErrorInterceptor already toasts on failure — the caller
	// just needs a boolean to decide whether to close the sidebar (design D3/D5).
	async reassignAgent(userId: string, agentId: string): Promise<boolean> {
		try {
			await firstValueFrom(this.service.reassignAgent(userId, agentId));
			return true;
		} catch {
			return false;
		}
	}

	async createAdmin(payload: ICreateAdminPayload) {
		const user = await firstValueFrom(this.service.createAdmin(payload));
		await this.listStore.fetch();
		return user;
	}
}
